'use client';

import { useEffect, useRef } from 'react';

import { storeBiometricCredentials } from './native-biometric-service';
import { readSessionFromCookies } from './native-supabase-client';
import { useNativeDetect } from './use-native-detect';

// Single source of truth for the localStorage key used to track biometric enrollment.
// Imported by use-biometric-auth.ts and native-biometric-service.ts to avoid drift.
export const BIOMETRIC_ENROLLED_KEY = 'biometric-enrolled';

function isBiometricEnrolled(): boolean {
  try {
    return localStorage.getItem(BIOMETRIC_ENROLLED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markBiometricEnrolled() {
  try {
    localStorage.setItem(BIOMETRIC_ENROLLED_KEY, 'true');
  } catch {
    // Ignore storage errors (private browsing, storage quota exceeded, etc.)
  }
}

export function clearBiometricEnrolled() {
  try {
    localStorage.removeItem(BIOMETRIC_ENROLLED_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Keeps biometric Keychain credentials in sync with the latest refresh token.
 *
 * Uses a localStorage flag to check enrollment status instead of calling
 * isCredentialsSaved — which triggers Face ID on iOS when credentials
 * are stored with accessControl: BIOMETRY_ANY.
 */
export function useBiometricTokenSync() {
  const { isNative } = useNativeDetect();
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isNative) return;

    async function syncKeychainFromCookies() {
      if (!isBiometricEnrolled()) return;

      const session = readSessionFromCookies();
      if (!session?.refresh_token || !session?.user?.id) return;

      if (lastTokenRef.current === session.refresh_token) return;
      lastTokenRef.current = session.refresh_token;

      await storeBiometricCredentials(session.user.id, session.refresh_token);
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        syncKeychainFromCookies();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    syncKeychainFromCookies();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isNative]);
}
